import { useCallback } from 'react';
import { ImportLineItem } from '../types';

let nextId = 100;
function generateId(): string {
  return `line-new-${nextId++}`;
}

function hasValue(v: string | number | null | undefined): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  return true;
}

export function useLineActions(
  lines: ImportLineItem[],
  setLines: (updater: (current: ImportLineItem[]) => ImportLineItem[]) => void,
  onMergeError?: (msg: string) => void
) {
  const addLine = useCallback(() => {
    setLines((prev) => {
      let lastPage = 1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].pdfRegion) {
          lastPage = prev[i].pdfRegion!.page;
          break;
        }
      }
      return [
        ...prev,
        {
          id: generateId(),
          article: '',
          designation: '',
          unite: '',
          quantite: null,
          prixUnitaire: null,
          confidence: 'high' as const,
          pdfRegion: { page: lastPage, x: 0, y: 0, width: 0, height: 0 },
        },
      ];
    });
  }, [setLines]);

  const deleteLine = useCallback(
    (id: string) => {
      setLines((prev) => prev.filter((l) => l.id !== id));
    },
    [setLines]
  );

  const updateLine = useCallback(
    (id: string, field: keyof ImportLineItem, value: string | number | null) => {
      setLines((prev) =>
        prev.map((l) => (l.id === id ? { ...l, [field]: value } : l))
      );
    },
    [setLines]
  );

  const mergeLine = useCallback(
    (id: string) => {
      // Pre-check outside setLines so we can call onMergeError
      const idx = lines.findIndex((l) => l.id === id);
      if (idx < 0 || idx >= lines.length - 1) return;
      const next = lines[idx + 1];

      const conflicts: string[] = [];
      if (hasValue(next.article)) conflicts.push('Article');
      if (hasValue(next.unite)) conflicts.push('Unité');
      if (hasValue(next.quantite)) conflicts.push('Quantité');
      if (hasValue(next.prixUnitaire)) conflicts.push('Prix unitaire');

      if (conflicts.length > 0) {
        onMergeError?.(
          `Fusion impossible : la ligne suivante contient des valeurs dans ${conflicts.join(', ')}. Seule la désignation peut être fusionnée.`
        );
        return;
      }

      setLines((prev) => {
        const i = prev.findIndex((l) => l.id === id);
        if (i < 0 || i >= prev.length - 1) return prev;
        const current = prev[i];
        const nxt = prev[i + 1];
        const merged: ImportLineItem = {
          ...current,
          designation: `${current.designation} ${nxt.designation}`.trim(),
          isMerged: true,
        };
        const result = [...prev];
        result.splice(i, 2, merged);
        return result;
      });
    },
    [lines, setLines, onMergeError]
  );

  const splitLine = useCallback(
    (id: string) => {
      setLines((prev) => {
        const idx = prev.findIndex((l) => l.id === id);
        if (idx < 0) return prev;
        const line = prev[idx];
        if (!line.isMerged) return prev;
        const words = line.designation.split(' ');
        const mid = Math.ceil(words.length / 2);
        const line1: ImportLineItem = {
          ...line,
          designation: words.slice(0, mid).join(' '),
          isMerged: false,
        };
        const line2: ImportLineItem = {
          id: generateId(),
          article: '',
          designation: words.slice(mid).join(' '),
          unite: '',
          quantite: null,
          prixUnitaire: null,
          confidence: line.confidence,
          pdfRegion: line.pdfRegion
            ? { ...line.pdfRegion, x: 0, y: 0, width: 0, height: 0 }
            : null,
          isMerged: false,
          classification: line.classification,
        };
        const result = [...prev];
        result.splice(idx, 1, line1, line2);
        return result;
      });
    },
    [setLines]
  );

  const reorderLines = useCallback(
    (activeId: string, overId: string) => {
      setLines((prev) => {
        const oldIndex = prev.findIndex((l) => l.id === activeId);
        const newIndex = prev.findIndex((l) => l.id === overId);
        if (oldIndex < 0 || newIndex < 0) return prev;
        const result = [...prev];
        const [moved] = result.splice(oldIndex, 1);
        result.splice(newIndex, 0, moved);
        return result;
      });
    },
    [setLines]
  );

  return { addLine, deleteLine, updateLine, mergeLine, splitLine, reorderLines };
}
